import Immutable from 'immutable';
import type { List, Map } from 'immutable';
import type { AnyAction, Reducer } from 'redux';
import { combineReducers } from 'redux-immutable';
import {
  ACTIVATE_VERSION,
  CREATE_LIVE_VERSION,
  GET_ITEM,
  GET_TEMPLATES,
  GET_VERSIONS,
  type Identifier,
} from './actions';

interface ActivationVersion {
  id: Identifier;
  active: number | boolean;
}

const activationDesignerReducer: Reducer<Map<string, unknown>, AnyAction> =
  Reflect.apply(combineReducers, undefined, [
    {
      templates: templatesReducer,
      items: itemsReducer,
      versions: versionsReducer,
    },
  ]);

export default activationDesignerReducer;


function templatesReducer(
  state: Map<string, unknown> = Immutable.fromJS({
    loading: false,
    data: undefined,
    errorMessage: undefined,
  }),
  action: AnyAction,
): Map<string, unknown> {

  switch (action.type) {
    case GET_TEMPLATES: {
      return state.merge(
        Immutable.fromJS({
          data: action.data.data,
          loading: false,
          errorMessage: undefined,
        }),
      );
    }
    case `${GET_TEMPLATES}_LOADING`: {
      return state.merge({
        data: undefined,
        loading: true,
        errorMessage: undefined,
      });
    }
    case `${GET_TEMPLATES}_ERROR`: {
      return state.merge({
        data: undefined,
        loading: false,
        errorMessage: JSON.stringify(action),
      });
    }
    default: {
      return state;
    }
  }
}

function versionsReducer(
  state: Map<string, unknown> = Immutable.fromJS({
    loading: false,
    data: undefined,
    errorMessage: undefined,
    creatingNewVersion: false,
    creatingLiveVersionId: undefined,
    activatingVersionId: undefined,
  }),
  action: AnyAction,
): Map<string, unknown> {

  switch (action.type) {
    case GET_VERSIONS: {
      return state.merge(
        Immutable.fromJS({
          data: action.data.data,
          loading: false,
          errorMessage: undefined,
        }),
      );
    }
    case ACTIVATE_VERSION: {
      const data = (
        state.get('data') as List<Map<string, unknown>>
      ).toJS() as ActivationVersion[];
      const entry = data.find((version) => version.id === action.id)!;
      entry.active = !entry.active ? 1 : 0;

      return state.merge(
        Immutable.fromJS({
          data,
          loading: false,
          errorMessage: undefined,
          activatingVersionId: undefined,
        }),
      );
    }
    case `${ACTIVATE_VERSION}_LOADING`: {
      return state.set('activatingVersionId', action.id);
    }
    case `${ACTIVATE_VERSION}_ERROR`: {
      return state.set('activatingVersionId', undefined);
    }
    case CREATE_LIVE_VERSION: {
      return state.merge({
        creatingNewVersion: false,
        creatingLiveVersionId: undefined,
      });
    }
    case `${CREATE_LIVE_VERSION}_LOADING`: {
      return state.merge({
        creatingNewVersion: true,
        creatingLiveVersionId: action.id,
      });
    }
    case `${CREATE_LIVE_VERSION}_ERROR`: {
      return state.merge({
        creatingNewVersion: false,
        creatingLiveVersionId: undefined,
      });
    }
    case `${GET_VERSIONS}_LOADING`: {
      return state.merge({
        data: undefined,
        loading: true,
        errorMessage: undefined,
      });
    }
    case `${GET_VERSIONS}_ERROR`: {
      return state.merge({
        data: undefined,
        loading: false,
        errorMessage: JSON.stringify(action),
      });
    }
    default: {
      return state;
    }
  }
}


function itemsReducer(
  state: Map<string, unknown> = Immutable.fromJS({
    loading: false,
    data: {},
    errorMessage: undefined,
  }),
  action: AnyAction,
): Map<string, unknown> {

  switch (action.type) {
    case GET_ITEM: {

      const newData = (
        state.get('data') as Map<string, unknown>
      ).toJS() as Record<string, unknown>;
      newData[action.id] = action.data.data;
      return state.merge(
        Immutable.fromJS({
          data: newData,
          loading: false,
          errorMessage: undefined,
        }),
      );
    }
    case `${GET_ITEM}_LOADING`: {
      const newData: object =
        (state.get('data') as Map<string, unknown> | undefined) || {};
      Reflect.set(newData, `${action.id}`, undefined);
      return state.merge({
        data: newData,
        loading: true,
        errorMessage: undefined,
      });
    }
    case `${GET_ITEM}_ERROR`: {
      const newData: object =
        (state.get('data') as Map<string, unknown> | undefined) || {};
      Reflect.set(newData, `${action.id}`, undefined);
      return state.merge({
        data: newData,
        loading: false,
        errorMessage: JSON.stringify(action),
      });
    }
    default: {
      return state;
    }
  }
}
